import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { APIEndpoint } from 'common/enum';
import { CustomerProfile } from 'common/interface';
import { universalPostRequest } from 'common/requestHandler';
import HeaderComp from 'components/elements/HeadingComponent';
import { userTypes } from 'config/config';
import { popupNotification } from 'notifications/ToastNotification';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import InputElement from '../../../components/elements/InputElement';
import { validationInviteCustomer } from './validation';

const InviteStaffUser = () => {
  const [loading, setLoading] = useState(false);
  const { uuid } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CustomerProfile>({
    resolver: yupResolver(validationInviteCustomer),
    mode: 'onChange',
    defaultValues: {
      email: '',
      phone_number: '',
      firstName: '',
      lastName: '',
    } as CustomerProfile,
  });

  const submitHandler = async (data: CustomerProfile) => {
    setLoading(true);
    const response = await universalPostRequest(APIEndpoint.inviteStaffUser, {
      email: data.email,
      phone_number: data.phone_number,
      userType: userTypes.companyStaff,
      firstName: data.firstName,
      lastName: data.lastName,
      companyId: uuid,
    });
    if ('error' in response) {
      popupNotification(response.error, false);
    } else {
      popupNotification(response.message, true);
      reset({} as CustomerProfile);
    }
    setLoading(false);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <Box>
      <HeaderComp>Invite staff user</HeaderComp>

      {/* Back Button */}
      <Box
        display="flex"
        alignItems="center"
        mb={4}
        sx={{
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateX(-4px)',
          },
        }}
        onClick={handleBackClick}
      >
        <IconButton
          size="small"
          sx={{
            backgroundColor: 'rgba(18, 112, 220, 0.1)',
            color: theme.palette.primary.main,
            mr: 1,
            '&:hover': {
              backgroundColor: 'rgba(18, 112, 220, 0.2)',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="body2"
          color="primary"
          sx={{
            fontWeight: 500,
            userSelect: 'none',
          }}
        >
          Back
        </Typography>
      </Box>

      <Box display="flex" justifyContent="center" width="100%">
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: '600px',
            background: '#ffffff',
            borderRadius: 2,
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
            p: 4,
          }}
        >
          {/* Form Title */}
          <Box textAlign="center" mb={4}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: '#000000',
                mb: 1,
              }}
            >
              Staff User Information
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#666666',
                fontSize: '0.875rem',
              }}
            >
              Please fill in the details below to invite a new staff member to
              your organization.
            </Typography>
          </Box>

          <form onSubmit={handleSubmit(submitHandler)}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#000000',
                      mb: 1,
                    }}
                  >
                    First name
                  </Typography>
                  <InputElement
                    name="firstName"
                    control={control}
                    placeholder="First name"
                    type="text"
                    rules={{ required: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        backgroundColor: '#ffffff',
                        '& fieldset': {
                          borderColor: '#e0e0e0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bdbdbd',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#000000',
                      mb: 1,
                    }}
                  >
                    Last name
                  </Typography>
                  <InputElement
                    name="lastName"
                    control={control}
                    placeholder="Last name"
                    type="text"
                    rules={{ required: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        backgroundColor: '#ffffff',
                        '& fieldset': {
                          borderColor: '#e0e0e0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bdbdbd',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#000000',
                      mb: 1,
                    }}
                  >
                    Email address
                  </Typography>
                  <InputElement
                    name="email"
                    control={control}
                    placeholder="Email address"
                    type="email"
                    rules={{ required: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        backgroundColor: '#ffffff',
                        '& fieldset': {
                          borderColor: '#e0e0e0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bdbdbd',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#000000',
                      mb: 1,
                    }}
                  >
                    Phone number
                  </Typography>
                  <InputElement
                    name="phone_number"
                    control={control}
                    placeholder="Phone number"
                    type="tel"
                    rules={{ required: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        backgroundColor: '#ffffff',
                        '& fieldset': {
                          borderColor: '#e0e0e0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bdbdbd',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>

            <Box mt={4}>
              <Button
                type="submit"
                variant="contained"
                size="small"
                fullWidth
                disabled={loading}
                sx={{
                  background: theme.palette.primary.main,
                  color: '#ffffff',
                  borderRadius: 1,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    background: theme.palette.primary.dark,
                  },
                  '&:disabled': {
                    background: '#bdbdbd',
                    color: '#666666',
                  },
                }}
              >
                {loading ? 'Inviting...' : 'Invite Staff User'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
    </Box>
  );
};

export default InviteStaffUser;
